import React, { useCallback, useRef, useState } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import { Pressable, PressableStateCallbackType, View } from 'react-native'

import BottomSheetGorhom from '@gorhom/bottom-sheet'
import styled, { useTheme } from 'styled-components/native'

import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import {
  InfoPopoverPlacement,
  InfoPopoverTriggerAction,
} from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'

import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { AddToCalendarIcon } from '../../atoms/icons/AddToCalendarIcon'
import { CalendarBookingIcon } from '../../atoms/icons/CalendarBookingIcon'
import { CloseCircleIcon } from '../../atoms/icons/CloseCircleIcon'
import { MenuDotsIcon } from '../../atoms/icons/MenuDotsIcon'
import { XIcon } from '../../atoms/icons/XIcon'
import { InfoPopover } from '../../atoms/infoPopover/InfoPopover'
import { Title, Size as TitleSize } from '../../atoms/title/Title'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { BottomSheet } from '../bottomSheet/BottomSheet'

type ActionMenuSession = Pick<Appointment, 'appointmentStatus'>

export const sessionActionMessages = defineMessages({
  rescheduleSession: {
    defaultMessage: 'Reschedule session',
    description: 'Accessibility label for the button that reschedules the appointment',
  },
  cancelSession: {
    defaultMessage: 'Cancel session',
    description: 'Accessibility label for the button that cancels the appointment',
  },
  openMenu: {
    defaultMessage: 'Open menu',
    description: 'Accessibility label for opening the menu',
  },
})

type CalendarCardActionMenuProps<Session extends ActionMenuSession = Appointment> = {
  addToCalendarLink?: (appointmentId: { appointmentId: number }) => void
  disallowSessionUpdate?: boolean
  handleCancelPress?: (appointment: Session) => void
  handleReschedulePress?: (appointment: Session) => void
  id?: number
  isTooLateToReschedule: boolean
  navigateToRescheduleSession?: (appointment: Session) => void
  onCancelSessionPress?: (appointment: Session) => void
  session: Session
}

const ActionMenuItem = styled(Pressable)({
  width: '100%',
})

const ActionMenuItemContent = styled.View<{ theme: ThemeType; hovered?: boolean }>(
  ({ theme: { breakpoints, colors, spacing }, hovered }) => ({
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing['8px'],
    paddingLeft: breakpoints.isMobileSized ? 0 : spacing['16px'],
    paddingRight: breakpoints.isMobileSized ? 0 : spacing['16px'],
    ...(hovered && !breakpoints.isMobileSized && { backgroundColor: colors.backgroundSection }),
  }),
)

const IconContainer = styled(Pressable)(({ theme }) => ({
  padding: theme.spacing['8px'],
}))

const MenuIconContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginRight: theme.spacing['8px'],
}))

const ManageSessionLinkContainer = styled.View<{ theme: ThemeType; first?: boolean }>(({ theme, first }) => ({
  marginTop: !first ? theme.spacing['16px'] : 0,
}))

const BottomSheetContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  padding: theme.spacing['24px'],
}))

const BottomSheetHeader = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing['24px'],
}))

const CloseButton = styled(Pressable)(({ theme }) => ({
  padding: theme.spacing['8px'],
}))

export const CalendarCardActionMenu = <Session extends ActionMenuSession = Appointment>({
  addToCalendarLink,
  disallowSessionUpdate,
  handleReschedulePress,
  handleCancelPress,
  id,
  isTooLateToReschedule,
  navigateToRescheduleSession,
  onCancelSessionPress,
  session,
}: CalendarCardActionMenuProps<Session>) => {
  const { breakpoints, colors } = useTheme()
  const { formatMessage } = useIntl()
  const [showBottomSheet, setShowBottomSheet] = useState(false)
  const bottomSheetRef = useRef<BottomSheetGorhom>(null)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)

  const openBottomSheet = useCallback(() => {
    bottomSheetRef.current?.expand()
  }, [])

  const closeBottomSheet = useCallback(() => {
    bottomSheetRef.current?.close()
    setShowBottomSheet(false)
  }, [])

  const openMenuAccessibilityLabel = formatMessage(sessionActionMessages.openMenu)

  const actionMenuContent = (
    <View>
      {addToCalendarLink && id !== undefined && session?.appointmentStatus !== 'canceled' && (
        <ManageSessionLinkContainer first={true}>
          <ActionMenuItem
            testID={tID('CalendarCard-add-to-calendar')}
            accessibilityRole='button'
            onPress={() => {
              addToCalendarLink({ appointmentId: id })
              if (breakpoints.isMobileSized) {
                closeBottomSheet()
              } else {
                setIsPopoverOpen(false)
              }
            }}
          >
            {({ hovered }: PressableStateCallbackType) => (
              <ActionMenuItemContent hovered={hovered}>
                <MenuIconContainer>
                  <AddToCalendarIcon fillColor={colors.textPrimary} />
                </MenuIconContainer>
                <BodyText
                  color={colors.textPrimary}
                  size={BodyTextSize.DEFAULT}
                  text={<FormattedMessage defaultMessage='Add to calendar' description='Add appointment to calendar' />}
                />
              </ActionMenuItemContent>
            )}
          </ActionMenuItem>
        </ManageSessionLinkContainer>
      )}
      {!isTooLateToReschedule &&
        (handleReschedulePress || navigateToRescheduleSession) &&
        session?.appointmentStatus !== 'canceled' &&
        !disallowSessionUpdate && (
          <ManageSessionLinkContainer first={!addToCalendarLink}>
            <ActionMenuItem
              testID={tID('CalendarCard-reschedule')}
              accessibilityRole='button'
              accessibilityLabel={formatMessage(sessionActionMessages.rescheduleSession)}
              onPress={() => {
                if (handleReschedulePress) {
                  handleReschedulePress(session)
                } else if (navigateToRescheduleSession) {
                  navigateToRescheduleSession(session)
                }
                if (breakpoints.isMobileSized) {
                  closeBottomSheet()
                } else {
                  setIsPopoverOpen(false)
                }
              }}
            >
              {({ hovered }: PressableStateCallbackType) => (
                <ActionMenuItemContent hovered={hovered}>
                  <MenuIconContainer>
                    <CalendarBookingIcon fillColor={colors.textPrimary} />
                  </MenuIconContainer>
                  <BodyText
                    color={colors.textPrimary}
                    size={BodyTextSize.DEFAULT}
                    text={
                      <FormattedMessage
                        defaultMessage='Reschedule'
                        description='Change the schedule of the appointment'
                      />
                    }
                  />
                </ActionMenuItemContent>
              )}
            </ActionMenuItem>
          </ManageSessionLinkContainer>
        )}
      {(handleCancelPress || onCancelSessionPress) &&
        session?.appointmentStatus !== 'canceled' &&
        !disallowSessionUpdate && (
          <ManageSessionLinkContainer
            first={isTooLateToReschedule && !!(handleReschedulePress || navigateToRescheduleSession)}
          >
            <ActionMenuItem
              testID={tID('CalendarCard-cancel')}
              accessibilityRole='button'
              accessibilityLabel={formatMessage(sessionActionMessages.cancelSession)}
              onPress={() => {
                if (handleCancelPress) {
                  handleCancelPress(session)
                } else if (onCancelSessionPress) {
                  onCancelSessionPress(session)
                }
                if (breakpoints.isMobileSized) {
                  closeBottomSheet()
                } else {
                  setIsPopoverOpen(false)
                }
              }}
            >
              {({ hovered }: PressableStateCallbackType) => (
                <ActionMenuItemContent hovered={hovered}>
                  <MenuIconContainer>
                    <CloseCircleIcon fillColor={colors.textError} />
                  </MenuIconContainer>
                  <BodyText
                    color={colors.textError}
                    size={BodyTextSize.DEFAULT}
                    text={<FormattedMessage {...sessionActionMessages.cancelSession} />}
                  />
                </ActionMenuItemContent>
              )}
            </ActionMenuItem>
          </ManageSessionLinkContainer>
        )}
    </View>
  )

  return breakpoints.isMobileSized ? (
    <>
      <IconContainer
        onPress={() => setShowBottomSheet(true)}
        testID={tID('CalendarCardActionMenu-trigger')}
        accessibilityRole='button'
        accessibilityLabel={openMenuAccessibilityLabel}
      >
        <MenuDotsIcon vertical />
      </IconContainer>
      {showBottomSheet && (
        <BottomSheet ref={bottomSheetRef} onCloseEnd={closeBottomSheet} snapPoints={[400]} useDynamicHeight>
          <BottomSheetContainer onLayout={openBottomSheet}>
            <BottomSheetHeader>
              <Title
                text={
                  <FormattedMessage
                    defaultMessage='Scheduling options'
                    description='Title for scheduling options bottom sheet'
                  />
                }
                size={TitleSize.MEDIUM}
              />
              <CloseButton onPress={closeBottomSheet} testID={tID('CalendarCard-menu-close')}>
                <XIcon size={24} fillColor={colors.iconDefault} />
              </CloseButton>
            </BottomSheetHeader>
            {actionMenuContent}
          </BottomSheetContainer>
        </BottomSheet>
      )}
    </>
  ) : (
    <InfoPopover
      content={actionMenuContent}
      offset={0}
      placement={InfoPopoverPlacement.BOTTOM_RIGHT}
      shouldDisplayCloseButton={false}
      trigger={
        <IconContainer
          accessibilityRole='button'
          accessibilityLabel={openMenuAccessibilityLabel}
          testID={tID('CalendarCardActionMenu-trigger')}
        >
          <MenuDotsIcon vertical />
        </IconContainer>
      }
      popoverTriggerAction={InfoPopoverTriggerAction.PRESS}
      popoverStyle={{ container: { width: '215px', paddingLeft: 0, paddingRight: 0 } }}
      isPopoverOpen={isPopoverOpen}
      setIsPopoverOpen={setIsPopoverOpen}
    />
  )
}
